import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config';
import CualappLogo from './CualappLogo';
import WalletComponent from './WalletComponent';
import TransactionsComponent from './TransactionsComponent';
import DomainsComponent from './DomainsComponent';
import MessagingComponent from './MessagingComponent';
import ActivityLogsComponent from './ActivityLogsComponent';
import EscrowComponent from './EscrowComponent';
import AdminPanel from './AdminPanel';
import MarketplaceComponent from './MarketplaceComponent';
import TasksComponent from './TasksComponent';
import InventoryComponent from './InventoryComponent';
import EconomyProfileComponent from './EconomyProfileComponent';
import CelulaComponent from './CelulaComponent';
import CualinmarketComponent from './CualinmarketComponent';
import JavierComponent from './JavierComponent';
import PagoMovilComponent from './PagoMovilComponent';
import AutomationsComponent from './AutomationsComponent';
import BlockchainComponent from './BlockchainComponent';
import GeolocationComponent from './GeolocationComponent';
import KYCComponent from './KYCComponent';
import TransportComponent from './TransportComponent';
import SmartEscrowComponent from './SmartEscrowComponent';
import PayoutsComponent from './PayoutsComponent';
import DisputesComponent from './DisputesComponent';
import AnalyticsComponent from './AnalyticsComponent';
import CUALjuegoComponent from './CUALjuegoComponent';
import CMTComponent from './CMTComponent';
import TierDashboardComponent from './TierDashboardComponent';
import CUALshareComponent from './CUALshareComponent';
import CUALgiftcardComponent from './CUALgiftcardComponent';
import TransportModule from './TransportModule';
import CUALtransporteComponent from './CUALtransporteComponent';
import NawaraComponent from './NawaraComponent';
import CUALstickerStore from './CUALstickerStore';
import CualencuestaComponent from './CualencuestaComponent';
import { playTone, showBrowserNotification } from './nawara/nawaraSound';
import OnboardingTour from './OnboardingTour';
import { INITIAL_TOUR, MODULE_TOURS } from './tourDefinitions';
import PaywallExplainerModal from './PaywallExplainerModal';
import { useI18n, LanguageSelector } from '../context/I18nContext';

const Dashboard = ({ user, onLogout }) => {
  const { t } = useI18n();
  const [activeModule, setActiveModule] = useState('domains');
  const [hasCelula, setHasCelula] = useState(true); // optimista, se valida en mount
  const [celulaLevel, setCelulaLevel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [visibleModules, setVisibleModules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showDiscover, setShowDiscover] = useState(false);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [completedTours, setCompletedTours] = useState([]);
  const [toursLoaded, setToursLoaded] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Paywall: chequear Celula activa + interceptor 402
  useEffect(() => {
    axios.get(`${API}/celula/status`).then(r => {
      setHasCelula(!!r.data?.has_celula);
      setCelulaLevel(r.data?.celula?.level || null);
    }).catch(() => { setHasCelula(false); });

    const interceptor = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err?.response?.status === 402 && err?.response?.data?.detail?.code?.startsWith('celula_')) {
          setActiveModule('celula');
          // Mensaje no intrusivo
          try {
            const msg = err.response.data.detail.message || 'Activa tu Celula para usar esta funcion';
            window.dispatchEvent(new CustomEvent('cualapp:paywall', { detail: { message: msg } }));
          } catch (e) {}
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const navHandler = (e) => {
      const target = e.detail?.module;
      if (target) setActiveModule(target);
    };
    window.addEventListener('cualapp:navigate', navHandler);
    return () => window.removeEventListener('cualapp:navigate', navHandler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications/`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(countRes.data.count || 0);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // WebSocket for real-time notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${new URL(BACKEND_URL).host}/ws/chat/${token}`;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && data.notification) {
            setNotifications(prev => [data.notification, ...prev].slice(0, 30));
            setUnreadCount(prev => prev + 1);
          }
          if (data.type === 'nawara_alert' && data.alert) {
            // Disparar zumbido + vibracion + notif del navegador
            try { playTone(data.alert.tone || 'buzz'); } catch (e) {}
            const isEmergency = !!data.alert.emergency;
            const title = isEmergency ? '🆘 NAWARA EMERGENCIA' : 'NAWARA';
            const body = isEmergency
              ? `${data.alert.caller_name} - SOS${data.alert.location ? ' (con ubicacion GPS)' : ''}`
              : `${data.alert.caller_name} te llama`;
            showBrowserNotification(title, body);
            const notif = {
              title,
              message: body,
              type: isEmergency ? 'error' : 'warning',
              link_module: 'nawara',
              read: false,
              created_at: data.alert.created_at,
            };
            setNotifications(prev => [notif, ...prev].slice(0, 30));
            setUnreadCount(prev => prev + 1);
            window.dispatchEvent(new CustomEvent('cualapp:nawara_alert', { detail: data.alert }));
          }
          if (data.type === 'nawara_response' && data.response) {
            window.dispatchEvent(new CustomEvent('cualapp:nawara_response', { detail: data.response }));
          }
          if (data.type === 'nawara_cancel' && data.cancel) {
            // Notificacion calmante: el emisor canceló su SOS
            showBrowserNotification(
              '✓ Falsa alarma',
              `${data.cancel.caller_name} canceló su SOS - está bien`
            );
            const notif = {
              title: '✓ Falsa alarma NAWARA',
              message: `${data.cancel.caller_name} cancelo su SOS - esta bien`,
              type: 'success',
              link_module: 'nawara',
              read: false,
              created_at: data.cancel.cancelled_at,
            };
            setNotifications(prev => [notif, ...prev].slice(0, 30));
            setUnreadCount(prev => prev + 1);
            window.dispatchEvent(new CustomEvent('cualapp:nawara_cancel', { detail: data.cancel }));
          }
        } catch (e) { /* ignore parse errors */ }
      };
      ws.onclose = () => {
        reconnectTimer = setTimeout(connect, 5000);
      };
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { /* ignore */ }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    setSearchOpen(true);
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/search/?q=${encodeURIComponent(value)}`);
        setSearchResults(res.data.results || []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (result.type === 'module') {
      setActiveModule(result.id);
    } else if (result.type === 'domain') {
      setActiveModule('domains');
    } else if (result.type === 'item' || result.type === 'action') {
      setActiveModule('inventory');
    } else if (result.type === 'listing') {
      setActiveModule('marketplace');
    } else if (result.type === 'user') {
      setActiveModule('messaging');
    }
  };

  const typeLabels = {
    module: 'Modulo', domain: 'Dominio', item: 'Item',
    action: 'Cual-Accion', user: 'Usuario', listing: 'CUALINMARKET'
  };

  const typeColors = {
    module: 'bg-purple-100 text-purple-700', domain: 'bg-blue-100 text-blue-700',
    item: 'bg-green-100 text-green-700', action: 'bg-amber-100 text-amber-700',
    user: 'bg-cyan-100 text-cyan-700', listing: 'bg-orange-100 text-orange-700'
  };

  const ADMIN_EMAILS = ["admin@cualapp.com", "test_domains@cualapp.com"];
  const isAdmin = ADMIN_EMAILS.includes(user?.email);

  // Fetch progressive modules
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await axios.get(`${API}/modules`);
        setVisibleModules(res.data.visible || []);
        setSuggestions(res.data.suggestions || []);
        setModulesLoaded(true);
      } catch (err) {
        setVisibleModules(fallbackModules);
        setModulesLoaded(true);
      }
    };
    fetchModules();
  }, [activeModule]);

  // Fetch completed tours
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const res = await axios.get(`${API}/modules/tours`);
        setCompletedTours(res.data.completed_tours || []);
        setToursLoaded(true);
      } catch (err) {
        setToursLoaded(true);
      }
    };
    fetchTours();
  }, []);

  // Trigger welcome tour for first-time users (skip si no tiene Celula: priorizar paywall flow)
  useEffect(() => {
    if (toursLoaded && modulesLoaded && !completedTours.includes('welcome') && !activeTour && hasCelula) {
      setTimeout(() => setActiveTour(INITIAL_TOUR), 1000);
    }
  }, [toursLoaded, modulesLoaded, completedTours, activeTour, hasCelula]);

  // Trigger module tour on first visit (skip si no tiene Celula)
  useEffect(() => {
    if (!toursLoaded || !modulesLoaded || activeTour || !hasCelula) return;
    const moduleTour = MODULE_TOURS[activeModule];
    if (moduleTour && !completedTours.includes(moduleTour.id)) {
      setTimeout(() => setActiveTour(moduleTour), 500);
    }
  }, [activeModule, toursLoaded, modulesLoaded, completedTours, activeTour, hasCelula]);

  const handleTourComplete = async (tourId) => {
    setActiveTour(null);
    setCompletedTours(prev => [...prev, tourId]);
    try { await axios.post(`${API}/modules/tours/${tourId}/complete`); } catch (err) {}
  };

  const handleTourSkip = async (tourId) => {
    setActiveTour(null);
    setCompletedTours(prev => [...prev, tourId]);
    try { await axios.post(`${API}/modules/tours/${tourId}/complete`); } catch (err) {}
  };

  // Icon mapping for module rendering
  const moduleIconMap = {
    'domains': 'domains', 'messaging': 'messaging', 'wallet': 'wallet',
    'economy': 'economy', 'inventory': 'inventory', 'marketplace': 'marketplace',
    'cualinmarket': 'cualinmarket', 'tasks': 'tasks', 'celula': 'celula',
    'pago_movil': 'pago_movil', 'automations': 'automations', 'blockchain': 'blockchain',
    'geo': 'geo', 'kyc': 'kyc', 'transport': 'transport', 'transactions': 'transactions',
    'escrow': 'escrow', 'smart_contract': 'smart_contract', 'payouts': 'payouts',
    'disputes': 'disputes', 'analytics': 'analytics', 'cualjuego': 'cualjuego',
    'mi_tier': 'mi_tier', 'cualshare': 'cualshare', 'logs': 'logs', 'javier': 'javier', 'admin_gear': 'admin_gear'
  };

  const fallbackModules = [
    { id: 'domains', name: 'Mis Dominios', icon: 'domains' },
    { id: 'messaging', name: 'CM/CC/CV', icon: 'messaging' },
    { id: 'wallet', name: 'Wallet', icon: 'wallet' },
    { id: 'economy_profile', name: 'Mi Economia', icon: 'economy' },
    { id: 'inventory', name: 'Mi Inventario', icon: 'inventory' },
    { id: 'marketplace', name: 'Marketplace', icon: 'marketplace' },
    { id: 'cualinmarket', name: 'CUALINMARKET', icon: 'cualinmarket' },
    { id: 'tasks', name: 'Mis Tareas', icon: 'tasks' },
    { id: 'celula', name: 'Mi Celula', icon: 'celula' },
    { id: 'pago_movil', name: 'Pago Movil', icon: 'pago_movil' },
    { id: 'cmt', name: 'CMT', icon: 'cmt' },
    { id: 'automations', name: 'Automatizaciones', icon: 'automations' },
    { id: 'blockchain', name: 'Blockchain', icon: 'blockchain' },
    { id: 'geo', name: 'Mapa', icon: 'geo' },
    { id: 'kyc', name: 'KYC', icon: 'kyc' },
    { id: 'transport', name: 'Transporte', icon: 'transport' },
    { id: 'cualtransporte', name: 'CUALtransporte', icon: 'cualtransporte' },
    { id: 'transactions', name: 'Transacciones', icon: 'transactions' },
    { id: 'escrow', name: 'Comercio P2P', icon: 'escrow' },
    { id: 'smart_escrow', name: 'Smart Contracts', icon: 'smart_contract' },
    { id: 'payouts', name: 'Payouts', icon: 'payouts' },
    { id: 'disputes', name: 'Disputas', icon: 'disputes' },
    { id: 'analytics', name: 'Analytics', icon: 'analytics' },
    { id: 'cualjuego', name: 'CUALjuego', icon: 'cualjuego' },
    { id: 'mi_tier', name: 'Mi Tier', icon: 'mi_tier' },
    { id: 'cualshare', name: 'CUALshare', icon: 'cualshare' },
    { id: 'cualgiftcard', name: 'CUALgiftcard', icon: 'cualgiftcard' },
    { id: 'nawara', name: 'NAWARA', icon: 'nawara' },
    { id: 'cualsticker_store', name: 'CUALsticker Store', icon: 'cualsticker_store' },
    { id: 'cualencuesta', name: 'CUALencuesta', icon: 'cualencuesta' },
    { id: 'logs', name: 'Bitacora', icon: 'logs' },
    ...(isAdmin ? [
      { id: 'javier', name: 'JAVIER', icon: 'javier' },
      { id: 'admin', name: 'Admin Panel', icon: 'admin_gear' }
    ] : [])
  ];

  const modules = modulesLoaded ? visibleModules : fallbackModules;

  const unlockModule = async (moduleId) => {
    try {
      await axios.post(`${API}/modules/unlock/${moduleId}`);
      const res = await axios.get(`${API}/modules`);
      setVisibleModules(res.data.visible || []);
      setSuggestions(res.data.suggestions || []);
      setActiveModule(moduleId);
    } catch (err) {
      console.error('Error unlocking module:', err);
    }
  };

  const renderActiveModule = () => {
    switch(activeModule) {
      case 'domains': return <DomainsComponent />;
      case 'messaging': return <MessagingComponent />;
      case 'wallet': return <WalletComponent />;
      case 'transactions': return <TransactionsComponent />;
      case 'economy_profile': return <EconomyProfileComponent />;
      case 'inventory': return <InventoryComponent />;
      case 'marketplace': return <MarketplaceComponent />;
      case 'cualinmarket': return <CualinmarketComponent />;
      case 'javier': return isAdmin ? <JavierComponent /> : <DomainsComponent />;
      case 'tasks': return <TasksComponent />;
      case 'celula': return <CelulaComponent />;
      case 'pago_movil': return <PagoMovilComponent user={user} />;
      case 'automations': return <AutomationsComponent user={user} />;
      case 'blockchain': return <BlockchainComponent user={user} />;
      case 'geo': return <GeolocationComponent user={user} />;
      case 'kyc': return <KYCComponent user={user} />;
      case 'transport': return <TransportModule user={user} />;
      case 'cualtransporte': return <CUALtransporteComponent user={user} />;
      case 'nawara': return <NawaraComponent user={user} />;
      case 'cualsticker_store': return <CUALstickerStore user={user} />;
      case 'cualencuesta': return <CualencuestaComponent user={user} />;
      case 'escrow': return <EscrowComponent />;
      case 'smart_escrow': return <SmartEscrowComponent />;
      case 'payouts': return <PayoutsComponent user={user} />;
      case 'disputes': return <DisputesComponent user={user} />;
      case 'analytics': return <AnalyticsComponent user={user} />;
      case 'cualjuego': return <CUALjuegoComponent user={user} />;
      case 'cmt': return <CMTComponent user={user} />;
      case 'mi_tier': return <TierDashboardComponent />;
      case 'cualshare': return <CUALshareComponent user={user} />;
      case 'cualgiftcard': return <CUALgiftcardComponent user={user} />;
      case 'logs': return <ActivityLogsComponent />;
      case 'admin': return isAdmin ? <AdminPanel /> : <DomainsComponent />;
      default: return <DomainsComponent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <CualappLogo size={36} animated={true} withWordmark={true} />
              <p className="text-xs text-gray-500">{user?.full_name}</p>
            </div>

            <div ref={searchRef} className="flex-1 max-w-2xl mx-4 sm:mx-8 relative" data-testid="search-bar-container">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
                  placeholder="Buscar en CUALAPP... dominios, items, usuarios, mercado"
                  data-testid="search-bar-input"
                  className="w-full pl-12 pr-20 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-full text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:bg-white focus:shadow-md transition-all duration-200"
                />
                <kbd className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:inline-flex px-2 py-0.5 text-xs font-mono bg-gray-200 text-gray-500 rounded-md border border-gray-300">Ctrl+K</kbd>
              </div>

              {searchOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50" data-testid="search-results-dropdown">
                  {searchLoading ? (
                    <div className="p-6 text-center text-gray-400">
                      <div className="animate-spin inline-block w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full mb-2"></div>
                      <p className="text-sm">Buscando...</p>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleResultClick(result)}
                          data-testid={`search-result-${idx}`}
                          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-purple-50 transition-colors text-left"
                        >
                          <span className="text-lg flex-shrink-0">{result.icon || '📄'}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{result.name}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${typeColors[result.type] || 'bg-gray-100 text-gray-600'}`}>
                            {typeLabels[result.type] || result.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-400">
                      <p className="text-sm">No se encontraron resultados para "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div ref={notifRef} className="relative">
                <button onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) fetchNotifications(); }}
                  data-testid="notification-bell" className="relative p-2 text-gray-500 hover:text-purple-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4.5 h-4.5 min-w-[18px] flex items-center justify-center rounded-full font-bold text-[10px] leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-hidden z-50" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-800">Notificaciones</h4>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} data-testid="mark-all-read-btn" className="text-xs text-purple-600 hover:text-purple-800 font-medium">
                          Marcar todas leidas
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-gray-400 text-sm">Sin notificaciones</div>
                      ) : (
                        notifications.map((n, i) => (
                          <button key={i} onClick={() => { if (n.link_module) { setActiveModule(n.link_module); setNotifOpen(false); } }}
                            data-testid={`notification-item-${i}`}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-purple-50/50' : ''}`}>
                            <div className="flex gap-2.5 items-start">
                              <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                                n.type === 'success' ? 'bg-green-500' : n.type === 'error' ? 'bg-red-500' : n.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                              }`} />
                              <div className="min-w-0">
                                <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}>{n.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-[10px] text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <LanguageSelector />
              <button onClick={onLogout} data-testid="logout-button" className="flex-shrink-0 text-gray-500 hover:text-red-500 text-sm font-medium transition-colors">
                Salir
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <nav className="space-y-1" data-testid="sidebar-nav">
              {modules.map((module) => (
                <button key={module.id} onClick={() => setActiveModule(module.id)} data-testid={`nav-${module.id}`}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left rounded-lg transition-all duration-150 ${
                    activeModule === module.id
                      ? 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border border-purple-200 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 border border-transparent'
                  }`}>
                  {module.icon === 'domains' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <circle cx="12" cy="12" r="9" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#7c3aed' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M12 3C8 8 8 16 12 21M12 3C16 8 16 16 12 21M3 12h18" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'wallet' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="2" y="6" width="20" height="14" rx="2" stroke={activeModule === module.id ? '#f59e0b' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#f59e0b' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M2 10h20" stroke={activeModule === module.id ? '#f59e0b' : '#6b7280'} strokeWidth="1"/>
                        <circle cx="17" cy="15" r="1.5" fill={activeModule === module.id ? '#f59e0b' : '#6b7280'}/>
                        <path d="M6 6V4a2 2 0 012-2h8a2 2 0 012 2v2" stroke={activeModule === module.id ? '#f59e0b' : '#6b7280'} strokeWidth="1.5"/>
                      </svg>
                    </span>
                  ) : module.icon === 'celula' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M7 11l5-7 5 7" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" strokeLinejoin="round" fill={activeModule === module.id ? '#6366f1' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="5" y="11" width="14" height="9" rx="1" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#6366f1' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="9" y="14" width="6" height="6" rx="0.5" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5"/>
                      </svg>
                    </span>
                  ) : module.icon === 'logs' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="4" y="3" width="16" height="18" rx="2" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#6366f1' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M8 7h8M8 11h6M8 15h4" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'transactions' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M7 10l5-5 5 5M17 14l-5 5-5-5" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'economy' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M3 17l4-4 4 4 4-8 4 4 2-2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3 21h18" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'inventory' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4M4 7l8 4M4 7v10l8 4m0-10v10" stroke={activeModule === module.id ? '#f59e0b' : '#6b7280'} strokeWidth="1.5" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'marketplace' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M3 3h18l-2 9H5L3 3z" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#ec4899' : '#6b7280'} fillOpacity="0.1" strokeLinejoin="round"/>
                        <circle cx="9" cy="19" r="1.5" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5"/>
                        <circle cx="17" cy="19" r="1.5" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5"/>
                        <path d="M5 12h14l-1 4H6l-1-4z" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'cualinmarket' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M3 21V7l9-4 9 4v14" stroke={activeModule === module.id ? '#f97316' : '#6b7280'} strokeWidth="1.5" strokeLinejoin="round" fill={activeModule === module.id ? '#f97316' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="8" y="14" width="8" height="7" stroke={activeModule === module.id ? '#f97316' : '#6b7280'} strokeWidth="1.5"/>
                        <path d="M12 14v7" stroke={activeModule === module.id ? '#f97316' : '#6b7280'} strokeWidth="1"/>
                      </svg>
                    </span>
                  ) : module.icon === 'tasks' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="4" y="4" width="16" height="16" rx="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M8 12l3 3 5-6" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'escrow' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="3" y="11" width="18" height="10" rx="2" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#6366f1' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="12" cy="16" r="1.5" fill={activeModule === module.id ? '#6366f1' : '#6b7280'}/>
                      </svg>
                    </span>
                  ) : module.icon === 'messaging' ? (
                    <span className="flex-shrink-0 w-6 h-6 relative">
                      <svg viewBox="0 0 28 28" fill="none" className="w-full h-full">
                        <path d="M4 6C4 4.9 4.9 4 6 4H22C23.1 4 24 4.9 24 6V18C24 19.1 23.1 20 22 20H16L10 25V20H6C4.9 20 4 19.1 4 18V6Z" fill={activeModule === module.id ? '#7c3aed' : '#9ca3af'} opacity="0.15" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5" strokeLinejoin="round"/>
                        <rect x="7" y="9" width="6" height="4" rx="0.8" fill={activeModule === module.id ? '#7c3aed' : '#6b7280'}/>
                        <circle cx="9" cy="14.5" r="1" fill="none" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1"/>
                        <path d="M8.2 15.5C8.2 15.5 8.6 16.3 9 16.3C9.4 16.3 9.8 15.5 9.8 15.5" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="0.8" strokeLinecap="round"/>
                        <path d="M16 10L20 10M16 13L20 13" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'javier' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <circle cx="12" cy="12" r="10" stroke={activeModule === module.id ? '#06b6d4' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#06b6d4' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M12 6v1M12 17v1M6 12h1M17 12h1" stroke={activeModule === module.id ? '#06b6d4' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="12" cy="12" r="3" stroke={activeModule === module.id ? '#06b6d4' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#06b6d4' : '#9ca3af'} fillOpacity="0.3"/>
                      </svg>
                    </span>
                  ) : module.icon === 'pago_movil' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="5" y="2" width="14" height="20" rx="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <circle cx="12" cy="18" r="1" fill={activeModule === module.id ? '#10b981' : '#6b7280'}/>
                        <path d="M9 8h6M9 11h4" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'cmt' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <circle cx="12" cy="12" r="9" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M12 3c-2.5 0-4.5 4-4.5 9s2 9 4.5 9 4.5-4 4.5-9-2-9-4.5-9z" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1" fill="none"/>
                        <path d="M3 12h18M4.5 7.5h15M4.5 16.5h15" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="0.8" strokeLinecap="round"/>
                        <path d="M9 12l3-4 3 4" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'automations' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                      </svg>
                    </span>
                  ) : module.icon === 'blockchain' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="3" y="3" width="7" height="7" rx="1" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="14" y="3" width="7" height="7" rx="1" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="3" y="14" width="7" height="7" rx="1" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <rect x="14" y="14" width="7" height="7" rx="1" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'geo' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <circle cx="12" cy="9" r="2.5" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5"/>
                      </svg>
                    </span>
                  ) : module.icon === 'kyc' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="3" y="4" width="18" height="16" rx="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <circle cx="9" cy="11" r="2.5" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5"/>
                        <path d="M5 17c0-2 2-3 4-3s4 1 4 3" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M15 9h4M15 12h3" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'transport' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="3" y="6" width="18" height="10" rx="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#10b981' : '#6b7280'} fillOpacity="0.1"/>
                        <circle cx="7" cy="18" r="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5"/>
                        <circle cx="17" cy="18" r="2" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1.5"/>
                        <path d="M3 10h18" stroke={activeModule === module.id ? '#10b981' : '#6b7280'} strokeWidth="1"/>
                      </svg>
                    </span>
                  ) : module.icon === 'smart_contract' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#6366f1' : '#6b7280'} fillOpacity="0.1" strokeLinejoin="round"/>
                        <path d="M14 2v6h6" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" strokeLinejoin="round"/>
                        <path d="M9 15l2 2 4-4" stroke={activeModule === module.id ? '#6366f1' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'payouts' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="2" y="5" width="20" height="14" rx="2" stroke={activeModule === module.id ? '#059669' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#059669' : '#6b7280'} fillOpacity="0.1"/>
                        <circle cx="12" cy="12" r="3" stroke={activeModule === module.id ? '#059669' : '#6b7280'} strokeWidth="1.5"/>
                        <path d="M12 10v1M12 13v1M11 12h2" stroke={activeModule === module.id ? '#059669' : '#6b7280'} strokeWidth="1" strokeLinecap="round"/>
                        <path d="M6 9V7M18 9V7" stroke={activeModule === module.id ? '#059669' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'disputes' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z" stroke={activeModule === module.id ? '#dc2626' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#dc2626' : '#6b7280'} fillOpacity="0.1" strokeLinejoin="round"/>
                        <path d="M12 10v3M12 16h.01" stroke={activeModule === module.id ? '#dc2626' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'analytics' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#7c3aed' : '#6b7280'} fillOpacity="0.1"/>
                        <path d="M7 17V13M12 17V9M17 17V7" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </span>
                  ) : module.icon === 'cualjuego' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#ec4899' : '#6b7280'} fillOpacity="0.1" strokeLinejoin="round"/>
                        <path d="M12 12l9-5.2M12 12v10M12 12L3 6.8" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
                        <circle cx="12" cy="10" r="2" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#ec4899' : '#6b7280'} fillOpacity="0.3"/>
                      </svg>
                    </span>
                  ) : module.icon === 'mi_tier' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" stroke={activeModule === module.id ? '#f59e0b' : '#6b7280'} strokeWidth="1.5" fill={activeModule === module.id ? '#f59e0b' : '#6b7280'} fillOpacity="0.15"/>
                      </svg>
                    </span>
                  ) : module.icon === 'cualshare' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                        <path d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" stroke={activeModule === module.id ? '#0d9488' : '#6b7280'} strokeWidth="1.5"/>
                      </svg>
                    </span>
                  ) : module.icon === 'cualgiftcard' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#ec4899' : '#6b7280'} strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                      </svg>
                    </span>
                  ) : module.icon === 'cualtransporte' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#4f46e5' : '#6b7280'} strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8m-8 5h6m-7 9V5a2 2 0 012-2h6.5L18 6.5V21M5 21h14M9 21v-4h6v4" />
                      </svg>
                    </span>
                  ) : module.icon === 'nawara' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#dc2626' : '#6b7280'} strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m5.108-.233l-2.105 2.105" />
                      </svg>
                    </span>
                  ) : module.icon === 'cualsticker_store' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
                      </svg>
                    </span>
                  ) : module.icon === 'cualencuesta' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </span>
                  ) : module.icon === 'admin_gear' ? (
                    <span className="flex-shrink-0 w-6 h-6">
                      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full" stroke={activeModule === module.id ? '#7c3aed' : '#6b7280'} strokeWidth="1.5">
                        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                    </span>
                  ) : (
                    <span className="text-lg">{module.icon}</span>
                  )}
                  <span className="text-sm font-medium">{module.name}</span>
                </button>
              ))}
            </nav>

            {/* Descubre - Contextual Suggestions */}
            {suggestions.length > 0 && (
              <div className="mt-4" data-testid="discover-section">
                <button onClick={() => setShowDiscover(!showDiscover)} data-testid="discover-toggle"
                  className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold text-gray-500 hover:text-purple-600 transition-colors">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                    </svg>
                    Descubre ({suggestions.length})
                  </span>
                  <svg className={`w-4 h-4 transition-transform ${showDiscover ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                {showDiscover && (
                  <div className="space-y-2 px-2 pb-2">
                    {suggestions.map(s => (
                      <button key={s.id} onClick={() => unlockModule(s.id)} data-testid={`suggestion-${s.id}`}
                        className="w-full p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-left hover:shadow-sm transition-all group">
                        <p className="text-xs font-semibold text-gray-800 group-hover:text-purple-700">{s.name}</p>
                        <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">{s.suggestion || s.description}</p>
                        <p className="text-[10px] text-amber-600 font-medium mt-1.5 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                          </svg>
                          Activar modulo
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <img src="/cualcoin-icon.png" alt="CUALcoin" className="w-8 h-8" data-testid="cualcoin-icon-sidebar" draggable="false" />
                <h3 className="text-sm font-semibold text-purple-700">CUALcoin (Cc)</h3>
              </div>
              <p className="text-xs text-purple-600 mt-1">Moneda nativa del ecosistema</p>
              <p className="text-xs text-purple-500 mt-1">1 CUALcoin = 1.000 Jing</p>
            </div>
            <div className="mt-3 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
              <h3 className="text-sm font-semibold text-cyan-700">13 Dominios</h3>
              <p className="text-xs text-cyan-600 mt-1">Tu vida digital organizada</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {['👨‍👩‍👧‍👦','🏠','🚗','👥','🤝','❤️','📚','🏭','💰','🖥️','🛒','🏗️','🧠'].map((icon, i) => (
                  <span key={i} className="text-xs">{icon}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-3" data-testid="main-content">
            {!hasCelula && activeModule !== 'celula' && (
              <div data-testid="paywall-banner" className="mb-4 rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{'\u{1F511}'}</span>
                  <div>
                    <p className="text-sm font-bold text-emerald-900">Activa tu Celula CUALAPP</p>
                    <p className="text-xs text-emerald-700">Puedes navegar el sistema, pero para enviar, publicar o transaccionar necesitas tu Celula. Desde solo $1 con el plan Semilla.</p>
                  </div>
                </div>
                <button onClick={() => { setActiveTour(null); setActiveModule('celula'); }}
                  data-testid="paywall-cta"
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 whitespace-nowrap">
                  Activar Celula
                </button>
              </div>
            )}
            {hasCelula && celulaLevel === 'semilla' && (
              <div data-testid="semilla-banner" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
                <p className="text-xs text-amber-800">
                  {'\u{1F331}'} Estas en plan Semilla. Algunas acciones avanzadas requieren un nivel superior.
                </p>
                <button onClick={() => setActiveModule('celula')}
                  data-testid="semilla-upgrade-cta"
                  className="px-3 py-1 rounded bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 whitespace-nowrap">
                  Mejorar
                </button>
              </div>
            )}
            {renderActiveModule()}
          </div>
        </div>
      </div>

      {/* Onboarding Tour */}
      {activeTour && (
        <OnboardingTour
          steps={activeTour.steps}
          tourId={activeTour.id}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}

      {/* Paywall explainer (P0-5): educa la primera vez que aparece un 402 */}
      <PaywallExplainerModal />
    </div>
  );
};

export default Dashboard;
